// src/shared/components/ErrorBoundary.tsx
import { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  /** True when error is a failed dynamic import (chunk 404 after deploy) */
  private isChunkLoadError = (): boolean => {
    const msg = this.state.error?.message ?? '';
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('dynamically imported module') ||
      msg.includes('Importing a module script failed')
    );
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  /** Full page reload to load new assets after deploy */
  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = this.isChunkLoadError();

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center"
          >
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {isChunkError ? 'نسخه جدید برنامه منتشر شده' : 'خطایی رخ داده است'}
            </h2>
            <p className="text-gray-600 mb-4">
              {isChunkError
                ? 'لطفاً صفحه را یک‌بار رفرش (به‌روزرسانی) کنید تا آخرین نسخه بارگذاری شود.'
                : 'متأسفانه مشکلی در نمایش این بخش پیش آمده است.'}
            </p>
            {this.state.error && !isChunkError && (
              <details className="mb-4 text-right">
                <summary className="cursor-pointer text-sm text-gray-500 mb-2">
                  جزئیات خطا
                </summary>
                <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto text-left">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <div className="flex gap-2 justify-center flex-wrap">
              {isChunkError ? (
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  رفرش صفحه
                </button>
              ) : (
                <button
                  onClick={this.handleReset}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  تلاش مجدد
                </button>
              )}
              <button
                onClick={() => (window.location.href = '/')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                بازگشت به صفحه اصلی
              </button>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
